
import { defineComponent, onBeforeMount, reactive, toRaw, watch } from 'vue'

// components
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { DeliveryCityT } from '@/core/types/delivery.types'

// composable
import useHelpers from '@/common/composable/useHelpers'

// realisation
import { delivery } from '@/core/realisations/delivery.realisation'

export default defineComponent({
    components: {
        'the-select': TheSelect,
        'the-button': TheButton,
    },
    props: {
        opened: {
            type: Boolean,
            default: false,
        },
        stepNumber: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        companyId: {
            type: Number,
            default: null,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const state = reactive({
            city: null as DeliveryCityT | null,
            city_id: null as number | null,
            is_city_selected: false,
        })

        const data_state = reactive({
            cities: new DataState<DeliveryCityT>({}),
        })

        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true
                const response = await delivery.getCities(page, search)
                data_state.cities.list = response.data

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadCities(1)
        })

        const loadMoreCities = async (page: number) => {
            if (page > data_state.cities.pagination.total_pages) return
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const response = await delivery.getCities(page)
                data_state.cities.list = data_state.cities.list.concat(response.data)

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const searchCities = (search_query: string) => {
            // data_state.companies.pagination.current_page
            loadCities(1, search_query)
        }

        const unshiftFindedCity = async (city: any) => {
            await loadCities(data_state.cities.pagination.current_page)
            data_state.cities.list.unshift(city)
        }

        const onCitySelected = (company_id: number) => {
            const { findObjectByValue } = useHelpers()
            const city = findObjectByValue('id', company_id, data_state.cities.list)

            if (!city) return
            state.city = city
        }

        const selectCity = () => {
            state.is_city_selected = true
            emit('submit', toRaw(state.city_id))
        }

        const resetSelectedCity = () => {
            state.is_city_selected = false
            state.city = null
            state.city_id = null

            emit('reset')
        }

        watch(
            () => props.companyId,
            (companyId) => {
                if (!companyId) {
                    state.is_city_selected = false
                    state.city_id = null
                    state.city = null
                }
            }
        )

        return {
            state,
            data_state,
            loadCities,
            loadMoreCities,
            searchCities,
            unshiftFindedCity,
            onCitySelected,
            selectCity,
            resetSelectedCity,
        }
    },
})
